<template>
  <div class="customercardnet">
    <div class="topsection">
      <div class="circle">
        <friendshipicon
          v-if="friendshipStateOverride"
          :friendname="customer.firstName"
          :state="friendshipStateOverride"
        />
        <friendicon
          v-else
          :friendname="customer.firstName"
          :state="customer.state"
          :multiple-friendships="customer.multipleVolunteers"
        />
      </div>
      <div class="details">
        <div class="name">
          <span>{{ customer.firstName }} {{ customer.lastName }}</span>
          <div class="usericons">
            <font-awesome-icon v-if="hasMultipleCustomers" icon="users" :title="$t('customerSearch.multipleCustomers')" />
          </div>
        </div>
        <div class="personal">
          <div>{{ genderAgeText }}</div>
          <div>{{ customerType }}</div>
          <div>{{ customer.customerTypeOtherInfo }}</div>
          <div v-if="customer.shortDescription" class="short-description">
            <font-awesome-icon icon="quote-left" aria-hidden="true" />
            <span>{{ customer.shortDescription }}</span>
            <font-awesome-icon icon="quote-right" aria-hidden="true" />
          </div>
          <div>{{ zipCode }} </div>
        </div>
      </div>
      <div v-show="customer.lastContacted" class="contacted">
        {{ lastContacted(customer.lastContacted) }}
      </div>
      <div class="nofriend">
        {{ daysWithoutFriend(customer.lastFriendshipEndDate) }}
      </div>
    </div>

    <div class="properties">
      <div class="row property genders">
        <div class="icon">
          <font-awesome-icon icon="venus-mars" />
        </div>
        <div class="content">
          <div>{{ $t('customerSearch.am') }} <strong>{{ gender.toLowerCase() || '...?' }}</strong></div>
          <div>{{ $t('customerSearch.forFriend') }}: <strong>{{ genderPreferences || '&ndash;' }}</strong></div>
        </div>
        <div v-if="showMatches" class="match">
          <tickcrosswarning v-model="matches.genderMatch" />
        </div>
      </div>

      <div class="row property age">
        <div class="icon">
          <font-awesome-icon icon="id-badge" />
        </div>
        <div class="content">
          <div>{{ $t('customerSearch.am') }} <strong>{{ age || '?' }} {{ $t('customerSearch.years') }}</strong></div>
          <div>{{ $t('customerSearch.forFriend') }}: <strong>{{ agePreferences || '&ndash;' }}</strong></div>
        </div>
        <div v-if="showMatches" class="match">
          <tickcross v-model="matches.ageMatch" />
        </div>
      </div>

      <div class="row property language">
        <div class="icon">
          <font-awesome-icon :icon="['far', 'comment']" />
        </div>
        <div class="content">
          <div>{{ $t('customerSearch.speak') }}: <strong>{{ normalizeTranslation(customer.nativeLanguage) }} ({{ $t('customerSearch.nativeLanguage') }})</strong></div>
          <div v-if="spokenLanguages">
            <strong>{{ spokenLanguages }}</strong>
          </div>
        </div>
        <div v-if="showMatches" class="match">
          <tickcross v-model="matches.languageMatch" />
        </div>
      </div>

      <div class="row property interest">
        <div class="icon">
          <font-awesome-icon icon="puzzle-piece" />
        </div>
        <div class="content">
          {{ $t('customerSearch.interestedIn') }}: <strong>{{ interests || '&ndash;' }}</strong>
        </div>
      </div>

      <div class="row property ecomms">
        <div class="icon">
          <font-awesome-icon icon="wifi" />
        </div>
        <div class="content">
          <dl v-if="customer.econtactDiscord" class="row">
            <dt class="col-12">
              {{ $t('connectivity.discord') }}
            </dt>
            <dd class="col-12">
              {{ customer.econtactDiscord }}
            </dd>
          </dl>
          <dl v-if="customer.econtactEmail" class="row">
            <dt class="col-12">
              {{ $t('connectivity.email') }}
            </dt>
            <dd class="col-12">
              {{ customer.econtactEmail }}
            </dd>
          </dl>
          <dl v-if="customer.econtactInstagram" class="row">
            <dt class="col-12">
              {{ $t('connectivity.instagram') }}
            </dt>
            <dd class="col-12">
              {{ customer.econtactInstagram }}
            </dd>
          </dl>
          <dl v-if="customer.econtactKik" class="row">
            <dt class="col-12">
              {{ $t('connectivity.kik') }}
            </dt>
            <dd class="col-12">
              {{ customer.econtactKik }}
            </dd>
          </dl>
          <dl v-if="customer.econtactSkype" class="row">
            <dt class="col-12">
              {{ $t('connectivity.skype') }}
            </dt>
            <dd class="col-12">
              {{ customer.econtactSkype }}
            </dd>
          </dl>
          <dl v-if="customer.econtactTelegram" class="row">
            <dt class="col-12">
              {{ $t('connectivity.telegram') }}
            </dt>
            <dd class="col-12">
              {{ customer.econtactTelegram }}
            </dd>
          </dl>
          <dl v-if="customer.econtactFreetext" class="row">
            <dt class="col-12">
              {{ $t('connectivity.freetext') }}
            </dt>
            <dd class="col-12">
              {{ customer.econtactFreetext }}
            </dd>
          </dl>
        </div>
      </div>
    </div>

    <div class="moreinfo">
      <div class="row">
        <div class="col description">
          <sprcol :heading="$t('profile.notes')">
            <div slot="body">
              <sprprivatenotes v-model="customer" endpoint="customerupdateprivatenotesnet" :friend-management-group-id="group.id" :target-id="targetId" is-customer is-net />
            </div>
          </sprcol>
        </div>
      </div>
      <div class="row">
        <div class="col contact">
          <sprcol :heading="$t('customerSearch.contactTitle')">
            <div slot="body">
              <p class="smalltext">
                {{ contactDetails }}
              </p>
            </div>
          </sprcol>
        </div>
      </div>
      <div class="row">
        <div class="col other">
          <sprcol :heading="$t('customerSearch.otherInfoTitle')">
            <div slot="body">
              <dl v-if="customer.customerApproval" class="row">
                <dt class="col-12">
                  {{ $t('customerForm.customerApproval') }}
                </dt>
                <dd class="col-12">
                  {{ customer.customerApprovedBy }}
                </dd>
              </dl>
              <dl v-if="customer.customerInterviewed" class="row">
                <dt class="col-12">
                  {{ $t('customerForm.customerInterviewed') }}
                </dt>
                <dd class="col-12">
                  {{ customer.customerInterviewedBy }}
                </dd>
              </dl>
              <dl v-if="customer.customerNotForSpr" class="row">
                <dt class="col-12">
                  {{ $t('customerForm.customerNotForSPR') }}
                </dt>
                <dd class="col-12">
                  {{ customer.customerNotForSprBy }}
                </dd>
              </dl>
              <dl v-if="customer.customerNotForPrivateRelations" class="row">
                <dt class="col-12">
                  {{ $t('customerForm.customerNotForPrivateRelations') }}
                </dt>
                <dd class="col-12">
                  {{ customer.customerNotForPrivateRelationsBy }}
                </dd>
              </dl>
              <dl v-if="contactPerson" class="row">
                <dt class="col-12">
                  {{ $t('customerForm.customerContactPerson') }}
                </dt>
                <dd class="col-12">
                  {{ contactPerson }}
                </dd>
              </dl>
              <dl v-if="customer.otherContact" class="row">
                <dt class="col-12">
                  {{ $t('customerForm.customerContactOther') }}
                </dt>
                <dd class="col-12">
                  {{ customer.otherContact }}
                </dd>
              </dl>
            </div>
          </sprcol>
        </div>
      </div>
      <div v-if="isOnHold" class="row">
        <div class="col onhold">
          <sprcol :heading="$t('volunteerForm.onHoldDescription')">
            <div slot="body">
              <dl class="row">
                <dt class="col-12">
                  {{ $t('customerSearch.onHoldUntilDate') }}
                </dt>
                <dd class="col-12">
                  {{ formattedOnHoldUntilDate }}
                </dd>
              </dl>
              <dl class="row">
                <dt class="col-12">
                  {{ $t('customerSearch.onHoldComment') }}
                </dt>
                <dd class="col-12">
                  {{ customer.onHoldComment }}
                </dd>
              </dl>
            </div>
          </sprcol>
        </div>
      </div>
    </div>

    <div v-if="showActions || showEditButton" class="row">
      <div class="col buttons">
        <!-- Actions -->
        <customerActions
          v-model="customer"
          :group="group"
          :loaded="loaded"
          :show-edit-button="showEditButton"
          :show-all-buttons="showActions"
          @saveCustomer="saveCustomer"
          @toggleActivity="toggleActivity"
        />
      </div>
    </div>
    <div v-if="isSending" class="text-center">
      <Loading />
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue'
  import { LocaleMessage } from 'vue-i18n'
  import * as _ from 'lodash'
  import friendicon from './FriendIcon.vue'
  import friendshipicon from './FriendshipIcon.vue'
  import tickcross from '../SPRTickCross.vue'
  import tickcrosswarning from '../SPRTickCrossWarning.vue'
  import sprcol from '../SPRCollapsible.vue'
  import sprprivatenotes from '../SPRPrivateNotes.vue'
  import customerActions from './CustomerActions.vue'
  import Loading from '../SPRLoading.vue'
  import moment from 'moment'

  import enumhelper from '../../mixins/enums.js'
  import datehelper from '../../mixins/dates.js'
  import locationhelper from '../../mixins/locationutils.js'

  import { Validator } from 'vee-validate'
  import validatordateisafter from '../../mixins/validator-dateisafter.js'
  import { CustomerState, FriendManagementDto, FriendCustomerNetDto, LanguageDto, FriendshipState } from '../../types/index'
  import { MatchDataNet } from '@/types/custom'
  import { finnishDateFormat } from '@/helpers/datehelper'

  // the data object
  export interface TemplateComponentData {
    loaded: boolean,
    isSending: boolean,
    customer: FriendCustomerNetDto
  }

  Validator.extend('dateisafter', validatordateisafter)

  export default Vue.extend({
    name: 'CustomerCardNet',
    components: { customerActions, friendicon, friendshipicon, tickcross, tickcrosswarning, sprcol, sprprivatenotes, Loading },
    mixins: [enumhelper, datehelper, locationhelper],
    props: {
      value: {
        type: Object as () => FriendCustomerNetDto, // Customer object
        required: true
      },
      group: {
        type: Object as () => FriendManagementDto,
        default: null
      },
      showMatches: {
        type: Boolean,
        default: () => {
          return false
        }
      },
      showActions: {
        type: Boolean,
        default: () => {
          return true
        }
      },
      showEditButton: {
        type: Boolean,
        default: () => {
          return false
        }
      },
      matches: {
        type: Object as () => MatchDataNet,
        default: () => {
          return {
            genderMatch: 'FAIL',
            ageMatch: false,
            languageMatch: false,
            interestMatch: false
          }
        }
      },
      friendshipStateOverride: {
        type: Number as () => FriendshipState,
        required: false,
        default: undefined
      }
    },
    data (): TemplateComponentData {
      return {
        customer: this.value,
        loaded: true,
        isSending: false
      }
    },
    computed: {
      isInactive (): boolean {
        return this.value.state === CustomerState.NonActive
      },
      hasMultipleCustomers (): boolean {
        return !!this.value?.multipleVolunteers
      },
      isOnHold (): boolean {
        return this.value.state === CustomerState.OnHold
      },
      gender (): string | LocaleMessage {
        // @ts-ignore - ts intellisense not finding mixins
        return this.getGenderTranslation(this.customer.gender)
      },
      formattedOnHoldUntilDate (): string {
        if (this.customer.onHoldUntilDate) {
          const date = this.customer.onHoldUntilDate
          const formattedDate = moment(date).format('DD.MM.YYYY')

          return formattedDate
        } else {
          return ''
        }
      },
      age (): number {
        // @ts-ignore - ts intellisense not finding mixins
        return this.calculateAge(this.customer.birthdate)
      },
      genderAgeText (): string | LocaleMessage {
        return [this.gender, this.age ? `${this.age} ${this.$t('customerSearch.years')}` : ''].filter(Boolean).join(', ')
      },
      customerType (): string {
        // @ts-ignore - ts intellisense not finding mixins
        return this.getNetCustomerTypesMaskTranslation(this.customer.customerTypes)
      },
      address (): string {
        // @ts-ignore - ts intellisense not finding mixins
        return this.formatAddress(this.customer.streetAddress, this.customer.zipCode, this.customer.city)
      },
      zipCode (): string {
        // @ts-ignore - ts intellisense not finding mixins
        return this.customer.zipCode
      },
      genderPreferences (): string | LocaleMessage {
        // @ts-ignore - ts intellisense not finding mixins
        return this.getGenderPreferencesMaskTranslation(this.customer.preferredGenders)
      },
      agePreferences (): string | LocaleMessage {
        // @ts-ignore - ts intellisense not finding mixins
        return this.getAgePreferencesMaskTranslation(this.customer.preferredAges)
      },
      spokenLanguages (): string {
        var self = this
        var languages = [] as string[]
        _.forEach(this.customer.customerLanguages, (item: LanguageDto) => {
          if (item.isoCode !== self.customer.nativeLanguage) {
            // @ts-ignore - ts intellisense not finding mixins
            languages.push(self.normalizeTranslation(item.isoCode))
          }
        })
        languages.push(this.customer.otherLanguages)
        return languages.filter(Boolean).join(', ')
      },
      interests (): string {
        var self = this
        // @ts-ignore - ts intellisense not finding mixins
        var interests = this.getInterestsMaskTranslation(self.customer.interests)
        if (this.customer.interestOther) {
          interests += `, ${this.customer.interestOther}`
        }
        return interests
      },
      contactDetails (): string {
        var phone = [this.customer.gsmPhone, this.customer.homePhone].filter(Boolean).join(' / ')
        var phoneText = phone ? `${this.$t('customerSearch.telAbbreviation')} ${phone}` : ''
        var email = this.customer.email ? `${this.$t('customerSearch.emailAbbreviation')}: ${this.customer.email}` : ''
        return [phoneText, email].filter(Boolean).join(' | ')
      },
      contactPerson (): string {
        var contactName = [this.customer.contactFirstName, this.customer.contactLastName].filter(Boolean).join(' ')
        var contactPhone = this.customer.contactPhonenumber ? `${this.$t('customerSearch.telAbbreviation')} ${this.customer.contactPhonenumber}` : ''
        // @ts-ignore - ts intellisense not finding mixins
        var contactAddress = this.formatAddress(this.customer.contactStreetAddress, this.customer.contactZipCode, this.customer.contactCity)
        return [this.customer.contactRelation,
                contactName,
                this.customer.contactEmail,
                contactPhone,
                contactAddress].filter(Boolean).join(' | ')
      },
      showEnable (): boolean {
        return this.customer.state === CustomerState.NonActive
      },
      showDisable (): boolean {
        return _.includes([CustomerState.PendingApproval, CustomerState.Active, CustomerState.OnHold], this.customer.state)
      },
      targetId (): number {
        return this.value.id
      }
    },
    watch: {
      value (newVal) {
        this.customer = newVal
      }
    },
    methods: {
      lastContacted (lastContactedDate: Date | undefined): string {
        if (!lastContactedDate) {
          return `${this.$t('customerSearch.neverContacted')}`
        }
        // @ts-ignore (no mixins found)
        return `${this.$t('customerSearch.lastContacted')} ${finnishDateFormat(lastContactedDate)}`
      },
      updateRemoteCustomer (): void {
        var self = this
        self.isSending = true
        var url = `${this.$store.state.apiBases.friendservice + this.$store.state.apiEndpoints.customerupdatenet[0]}`
        this.$http.put(url, this.customer).then(response => {
          if (response.ok) {
            self.isSending = false
            this.notifyRefresh()
          }
        }, response => {
          // @ts-ignore - mixins not picked up
          this.showAlertError(`${this.$t('common.saveFailed')}`)
          console.error('Error saving customer state')
        })
      },
      daysWithoutFriend (lastFriendshipDate): string {
        if (!lastFriendshipDate) {
          return `${this.$t('customerSearch.neverHadFriend')}`
        }
        if (this.customer.friendships.filter(friendship => friendship.friendshipState === FriendshipState.Active).length > 0) {
          // @ts-ignore (mixin not found)
          return `${this.$t('customerSearch.hasActiveFriendship')}` // we might want to change this to a different message in the future
        }
        // @ts-ignore (no mixins found)
        return `${this.calculateDayDiff(lastFriendshipDate)} ${this.$t('customerSearch.daysWithoutFriend')}`
      },
      saveCustomer (): void {
        this.updateRemoteCustomer()
      },
      notifyRefresh (): void {
        this.$emit('refresh')
      },
      toggleActivity (): void {
        var uri = this.$store.state.apiBases.friendservice
        if (this.customer.state === CustomerState.NonActive) {
          uri += this.$store.state.apiEndpoints.markactivecustomernet[0]
        } else {
          uri += this.$store.state.apiEndpoints.markinactivecustomernet[0]
        }

        uri = uri
          .replace('{customerid}', this.customer.id)
          .replace('{groupid}', this.group.id)

        const self = this

        this.$http.post(uri).then(() => {
          this.notifyRefresh()
        })
      }
    }
  })
</script>

<style lang="scss">
  @import "../../assets/scss/_variables.scss";

  .customercardnet {
    margin-bottom: 30px;

    .usericons {
        display: inline-block;
        margin-left: 5px;
        color: $brand-red;
      }

    .topsection {
      min-height: 120px;
      .circle {
        float: left;
        margin-top:20px;
      }

      .contacted {
        text-align: center;
        font-size: 13px;
        margin-top: 15px;
      }

      .details {
        margin-left:70px;

        .name {
          text-transform: uppercase;
          color: $brand-red;
          font-weight: bold;
          position: relative;
          .edit {
            position: absolute;
            left:-15px;
            text-decoration: none;
            color:$brand-red;
          }
        }

        .personal {
          font-size: 0.8em;
          font-weight:bold;
          .short-description {
            font-weight:normal;
            color:$brand-red;
            font-style: italic;
            margin-top: 10px;
            position:relative;
            span {
              padding: 0 5px;
            }
            svg {
              font-size:12px;
              display:inline-block;
            }
          }
        }
      }
      .nofriend {
        text-align: center;
        font-size: 13px;
        margin-top: 15px;
      }
    }

    .properties {
      clear: both;
      margin-top: 10px;
      margin-bottom: 30px;

      .property {
        display: flex;
        flex-flow: row nowrap;
        justify-content: space-between;
        align-items: center;

        min-height: 55px;
        margin: 0;
        border-bottom: solid 1px $brand-grey3;
        padding: 5px 0;

        .icon {
          flex: 0 0 40px;
          text-align: center;

          font-size: 2em;
          color:$brand-red;
        }
        //IE11
        *::-ms-backdrop, .icon { margin-top: 15px; }

        .content {
          margin-left: 10px;
          flex: auto;

          font-size: 0.8em;
          dl {
            margin-bottom: 0;
            dt {
              font-size: 1em;
            }
          }
        }

        .match {
          flex: 0 0 40px;
          text-align: right;

          font-size: 1.5em;
        }
      }
    }

    button {
      margin-bottom: 15px;
      &.button-disabled {
        border-color: $brand-grey4;
        color: $brand-grey4;
        cursor: not-allowed;
        outline: none;
        &:hover {
          background-color:$brand-white;
        }
      }
    }

    .moreinfo {
      .description,
      .contact,
      .other {
        margin-bottom: 20px;
      }
      dl {
        margin-bottom: 0;
        font-size: 0.8em;
        dt {
          font-size: 1em;
        }
      }
    }

    .edit-customer,
    .enable-customer,
    .disable-customer,
    .onhold-customer,
    .onholdinfo {
      text-align: center;
      margin-top: 15px;
    }

    .onholdinfo {
      text-align:left;

      .holddate {
        margin-bottom:15px;
      }

      a {
        margin-top:14px;
        display:block;
      }
    }
  }
</style>
